import { useState, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { taskDeliverableService } from '@/services/task-deliverable.service';
import { ClipboardCheck, Plus, Trash2, Loader2 } from 'lucide-react';
import type { TaskDeliverable } from '@/types/api';

interface TaskDeliverableSectionProps {
  taskId: string;
  deliverables: TaskDeliverable[];
  onChange: () => void;
}

export function TaskDeliverableSection({ taskId, deliverables, onChange }: TaskDeliverableSectionProps) {
  const { toast } = useToast();
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const total = deliverables.length;
  const doneCount = deliverables.filter(d => d.done).length;

  const handleAdd = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setAdding(true);
    try {
      await taskDeliverableService.create(taskId, { title });
      setNewTitle('');
      onChange();
    } catch (error: any) {
      toast({
        title: 'Error adding deliverable',
        description: error?.response?.data?.error || error?.message,
        variant: 'destructive',
      });
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (deliverable: TaskDeliverable) => {
    setTogglingId(deliverable.id);
    try {
      await taskDeliverableService.update(deliverable.id, { done: !deliverable.done });
      onChange();
    } catch (error: any) {
      toast({
        title: 'Error updating deliverable',
        description: error?.response?.data?.error || error?.message,
        variant: 'destructive',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (deliverable: TaskDeliverable) => {
    setDeletingId(deliverable.id);
    try {
      await taskDeliverableService.delete(deliverable.id);
      onChange();
    } catch (error: any) {
      toast({
        title: 'Error deleting deliverable',
        description: error?.response?.data?.error || error?.message,
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Deliverables</p>
        </div>
        {total > 0 && (
          <span className="text-xs text-muted-foreground">
            {doneCount}/{total} done
          </span>
        )}
      </div>

      {deliverables.length > 0 && (
        <ul className="space-y-1 mb-2">
          {deliverables.map(d => (
            <li
              key={d.id}
              className="group flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/40"
            >
              <Checkbox
                checked={d.done}
                disabled={togglingId === d.id}
                onCheckedChange={() => handleToggle(d)}
              />
              <span className={`flex-1 text-sm ${d.done ? 'line-through text-muted-foreground' : ''}`}>
                {d.title}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                onClick={() => handleDelete(d)}
                disabled={deletingId === d.id}
                title="Delete"
              >
                {deletingId === d.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Input
          placeholder="Add a deliverable…"
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-8 text-sm"
          disabled={adding}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={handleAdd}
          disabled={adding || !newTitle.trim()}
          className="h-8 px-3"
        >
          {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}
